import ArrowBackIosNewIcon from "@mui/icons-material/ArrowBackIosNew";
import Button from "components/common/Button";
import Input from "components/common/Input";
import TextArea from "components/common/TextAreawithLabel";
import { useAppDispatch, useAppSelector } from "hooks/store";
import { useEffect, useMemo, useState } from "react";
import { toast } from "react-toastify";
import { placeOrder, updateCheckoutData } from "store/slices/main.slice";
import { ORDER } from "utils/constants";
import CreditCardOutlinedIcon from "@mui/icons-material/CreditCardOutlined";
import StorefrontOutlinedIcon from "@mui/icons-material/StorefrontOutlined";
import AccountBalanceWalletOutlinedIcon from "@mui/icons-material/AccountBalanceWalletOutlined";
import {
  fetchDeliveryChargesForAddress,
  getFixedDeliveryCharge,
  isKmDeliveryChargesEnabled,
} from "utils/deliveryCharges";
import { getUnavailableCartItemsForOrderType } from "utils/orderTypeAvailability";

type Props = {
  setStage: (v: string) => void;
  setShowPopup: (v: boolean) => void;
  setOrderTotal: (v: number) => void;
};

const joinAddressParts = (...parts: Array<string | null | undefined>) =>
  parts
    .map((v) => String(v || "").trim().replace(/(^,|,$)/g, ""))
    .filter(Boolean)
    .join(", ");

const CHECKOUT_FONT_FAMILY =
  "Manrope, Inter, ui-sans-serif, system-ui, -apple-system, Segoe UI, Roboto, Helvetica, Arial, 'Apple Color Emoji', 'Segoe UI Emoji'";

const CheckoutView = ({ setStage, setShowPopup, setOrderTotal }: Props) => {
  const [tip, setTip] = useState(0);
  const [customTipText, setCustomTipText] = useState("0");
  const [notes, setnotes] = useState("");
  const [selectedPayment, setSelectedPayment] = useState<string>("");
  const [kmDeliveryCharge, setKmDeliveryCharge] = useState<number | null>(null);
  const [deliveryDistance, setDeliveryDistance] = useState<number | null>(null);
  const dispatch = useAppDispatch();
  const checkoutData = useAppSelector(({ main }) => main.checkoutData);
  const { settings, companyData } = useAppSelector(({ main }) => main);

  useEffect(() => {
    if (checkoutData?.tip && !tip) {
      setTip(checkoutData.tip);
      setCustomTipText(String(checkoutData.tip));
    }
  }, [checkoutData, tip]);

  const updateTip = (v: number) => {
    const nextTip = +Number(v || 0).toFixed(2);
    setTip(nextTip);
    setCustomTipText(String(nextTip));
    dispatch(updateCheckoutData({ tip: nextTip }));
  };

  const updateCustomTip = (value: string) => {
    if (!/^\d*\.?\d{0,2}$/.test(value)) return;

    setCustomTipText(value);
    const nextTip = Number(value);
    const safeTip = Number.isFinite(nextTip) ? nextTip : 0;
    setTip(safeTip);
    dispatch(updateCheckoutData({ tip: safeTip }));
  };

  const normalizeCustomTip = () => {
    const nextTip = +Number(customTipText || 0).toFixed(2);
    setTip(nextTip);
    setCustomTipText(String(nextTip));
    dispatch(updateCheckoutData({ tip: nextTip }));
  };

  const orderType = useMemo(
    () => checkoutData?.orderType,
    [checkoutData?.orderType]
  );
  const unavailableCartItems = useMemo(
    () => getUnavailableCartItemsForOrderType(checkoutData?.cart || [], orderType),
    [checkoutData?.cart, orderType]
  );

  const showUnavailableItemsError = () => {
    const itemNames = unavailableCartItems
      .map(({ item }) => item?.fullDescription || item?.productName || item?.name)
      .filter(Boolean)
      .join(", ");
    toast.error(
      `Some items are not available for ${
        orderType === ORDER.DELIVERY ? "Delivery" : "Pickup"
      }: ${itemNames}`
    );
  };

  const allowPayAtCounter = useMemo(() => {
    if (orderType === ORDER.TAKE_OUT) {
      return !settings.DontPayAtStorePickup;
    }
    if (orderType === ORDER.DELIVERY) {
      return !settings.DontPayAtStoreDelivery;
    }

    return true;
  }, [settings, orderType]);

  const allowOnlinePayment = useMemo(() => {
    if (orderType === ORDER.TAKE_OUT) {
      return !!settings.PickupOnlinePayment;
    }
    if (orderType === ORDER.DELIVERY) {
      return !!settings.DeliveryOnlineOrder;
    }

    return true;
  }, [settings, orderType]);

  const specialDiscount = useMemo(() => {
    if (
      !(
        (settings.AllowDiscountOnDelivery && orderType === ORDER.DELIVERY) ||
        (settings.AllowDiscountOnPickup && orderType === ORDER.TAKE_OUT)
      )
    )
      return 0;

    const subTotal = Number(checkoutData?.finalBill?.[0]?.amt || 0);
    return +((subTotal * +settings.OnlineSpecialDiscount) / 100).toFixed(2);
  }, [settings, orderType, checkoutData]);

  const deliveryCharge = useMemo(() => {
    if (orderType !== ORDER.DELIVERY) return 0;
    if (isKmDeliveryChargesEnabled(settings)) {
      return kmDeliveryCharge ?? Number(checkoutData?.deliveryCharge || 0);
    }

    return getFixedDeliveryCharge(companyData);
  }, [orderType, settings, kmDeliveryCharge, checkoutData?.deliveryCharge, companyData]);

  const checkoutDeliveryAddress = useMemo(
    () =>
      String(
        checkoutData?.selectedAddress === "office"
          ? checkoutData?.address1 || ""
          : checkoutData?.address || ""
      ).trim(),
    [checkoutData?.address, checkoutData?.address1, checkoutData?.selectedAddress]
  );

  useEffect(() => {
    if (orderType !== ORDER.DELIVERY || !isKmDeliveryChargesEnabled(settings)) {
      setKmDeliveryCharge(null);
      setDeliveryDistance(null);
      return;
    }

    if (!checkoutDeliveryAddress) return;

    let isCancelled = false;
    fetchDeliveryChargesForAddress(checkoutDeliveryAddress)
      .then((result) => {
        if (isCancelled) return;
        setKmDeliveryCharge(result.charges);
        setDeliveryDistance(result.distance);
        dispatch(
          updateCheckoutData({
            deliveryCharge: result.charges,
            deliveryDistance: result.distance,
          })
        );
      })
      .catch((e) => console.error("Failed to load delivery charges by km", e));

    return () => {
      isCancelled = true;
    };
  }, [checkoutDeliveryAddress, dispatch, orderType, settings]);

  const checkoutGrandTotal = useMemo(
    () => +(checkoutData.total - specialDiscount + tip + deliveryCharge).toFixed(2),
    [checkoutData.total, deliveryCharge, specialDiscount, tip]
  );

  const scheduledLabel = useMemo(() => {
    if (!checkoutData?.onDate || !checkoutData?.onTime) return "";

    try {
      const start = new Date(`${checkoutData.onDate}T${checkoutData.onTime}`);
      const end = new Date(start.getTime() + 10 * 60 * 1000);
      return `${start.getMonth() + 1}/${start.getDate()} ${start.toLocaleTimeString([], {
        hour: "numeric",
        minute: "2-digit",
      })}-${end.toLocaleTimeString([], {
        hour: "numeric",
        minute: "2-digit",
      })}`;
    } catch {
      return `${checkoutData.onDate} ${checkoutData.onTime}`;
    }
  }, [checkoutData?.onDate, checkoutData?.onTime]);

  const customerName = useMemo(
    () => [checkoutData?.firstName, checkoutData?.lastName].filter(Boolean).join(" "),
    [checkoutData?.firstName, checkoutData?.lastName]
  );

  const customerAddress = useMemo(() => {
    if (orderType === ORDER.DELIVERY) {
      return checkoutData?.selectedAddress === "office"
        ? joinAddressParts(checkoutData?.address1)
        : joinAddressParts(checkoutData?.address);
    }

    return joinAddressParts(companyData?.address1, companyData?.address2);
  }, [checkoutData?.address, checkoutData?.address1, checkoutData?.selectedAddress, companyData, orderType]);

  const handlePlaceOrder = () => {
    if (unavailableCartItems.length) {
      showUnavailableItemsError();
      return;
    }

    dispatch(
      placeOrder({
        tip,
        deliveryCharge,
        paymentType: "oncounter",
        setStage,
        notes: notes,
        setShowPopup,
        setOrderTotal,
      }) as unknown as any
    );
  };

  const paymentOptions = useMemo(() => {
    const items: Array<{
      id: string;
      label: string;
      kind: "counter" | "online";
      icon: JSX.Element;
      description: string;
    }> = [];

    if (allowPayAtCounter) {
      items.push({
        id: "oncounter",
        label: "Pay at Counter",
        kind: "counter",
        icon: <StorefrontOutlinedIcon className="!text-[20px]" />,
        description: "Pay when you arrive at the store.",
      });
    }

    if (allowOnlinePayment) {
      items.push({
        id: "pay_pal",
        label: "PayPal",
        kind: "online",
        icon: <AccountBalanceWalletOutlinedIcon className="!text-[20px]" />,
        description: "Continue to PayPal secure payment.",
      });
      items.push({
        id: "clover",
        label: "Credit or Debit Card",
        kind: "online",
        icon: <CreditCardOutlinedIcon className="!text-[20px]" />,
        description: "Continue to card payment.",
      });
    }

    return items;
  }, [allowOnlinePayment, allowPayAtCounter]);

  const selectedPaymentOption = useMemo(
    () => paymentOptions.find((option) => option.id === selectedPayment),
    [paymentOptions, selectedPayment]
  );

  useEffect(() => {
    if (!selectedPayment && paymentOptions.length) {
      const firstPaymentId = paymentOptions[0]?.id;
      if (firstPaymentId) setSelectedPayment(firstPaymentId);
    }
  }, [paymentOptions, selectedPayment]);

  // === ADDED BY SAMPATH – 2026-01-15 ===
  // Tip preset amounts + active state helpers (keeps UI consistent and allows non-theme styling)
  const tip10 = useMemo(
    () => +((checkoutData?.total * 10) / 100).toFixed(2),
    [checkoutData?.total]
  );
  const tip15 = useMemo(
    () => +((checkoutData?.total * 15) / 100).toFixed(2),
    [checkoutData?.total]
  );
  const tip20 = useMemo(
    () => +((checkoutData?.total * 20) / 100).toFixed(2),
    [checkoutData?.total]
  );
  const isTipActive = (v: number) => Math.abs((tip || 0) - v) < 0.01;
  // === END SAMPATH ===

  if (!checkoutData) return <></>;
  return (
    <>
      <style>
        {`
          .checkoutView {
            background: #f8f8f6;
            height: 100%;
            font-family: ${CHECKOUT_FONT_FAMILY};
            color: #101828;
            min-height: 0;
            overflow: hidden;
          }
          .checkoutHeader {
            background: #ffffff;
            min-height: 56px;
          }
          .checkoutShell {
            display: grid;
            grid-template-columns: minmax(0, 1fr) 360px;
            min-height: calc(100% - 52px);
            height: calc(100% - 52px);
          }
          .checkoutMain {
            overflow: auto;
            padding: 24px 28px 28px;
          }
          .checkoutSide {
            border-left: 1px solid rgba(15, 23, 42, 0.12);
            background: #fff;
            padding: 20px 22px 28px;
            overflow: auto;
          }
          .checkoutCard {
            background: #ffffff;
            border: 1px solid rgba(15, 23, 42, 0.08);
            border-radius: 16px;
            box-shadow: 0 10px 24px rgba(15, 23, 42, 0.05);
            padding: 18px 20px;
          }
          .checkoutPaymentGrid {
            display: grid;
            grid-template-columns: repeat(2, minmax(0, 1fr));
            gap: 12px;
            margin-top: 16px;
          }
          .checkoutPaymentCard {
            border: 1px solid rgba(15, 23, 42, 0.10);
            border-radius: 18px;
            padding: 14px;
            background: rgba(255,255,255,.88);
            text-align: left;
            transition: .2s ease;
          }
          .checkoutPaymentCardActive {
            border-color: rgba(17,24,39,.82);
            background: linear-gradient(180deg, rgba(17,24,39,.96), rgba(31,41,55,.96));
            color: #fff;
            box-shadow: 0 18px 28px rgba(15,23,42,.18);
          }
          .checkoutPaymentCardActive .checkoutMuted {
            color: rgba(255,255,255,.78);
          }
          .checkoutSection {
            display: grid;
            grid-template-columns: 150px minmax(0, 1fr);
            gap: 16px;
            padding: 20px 0;
            border-top: 1px solid rgba(15, 23, 42, 0.12);
          }
          .checkoutSection:first-child {
            border-top: 0;
            padding-top: 0;
          }
          .checkoutSectionTitle {
            font-size: 15px;
            font-weight: 800;
            color: #101828;
          }
          .checkoutSectionBody {
            min-width: 0;
          }
          .checkoutContactCard {
            display: flex;
            flex-direction: column;
            gap: 4px;
            color: #101828;
          }
          .checkoutMethodList {
            display: flex;
            flex-direction: column;
            gap: 12px;
          }
          .checkoutMethodItem {
            display: flex;
            align-items: center;
            justify-content: space-between;
            border-top: 1px solid rgba(15, 23, 42, 0.10);
            padding: 16px 0;
            font-size: 16px;
            font-weight: 600;
            color: #101828;
          }
          .checkoutMethodItem:first-child {
            border-top: 0;
            padding-top: 0;
          }
          .checkoutRow {
            padding: 10px 0;
            border-bottom: 1px solid rgba(15, 23, 42, 0.08);
          }
          .checkoutRow:last-child {
            border-bottom: none;
          }
          .checkoutMuted {
            color: rgba(15, 23, 42, 0.70);
          }
          .checkoutTotalBar {
            background: #ffffff !important;
            border: 1px solid rgba(15, 23, 42, 0.08) !important;
            border-radius: 14px !important;
            box-shadow: 0 10px 24px rgba(0,0,0,0.04) !important;
            padding: 14px 16px !important;
          }
          .checkoutTotalBar p:first-child {
            color: #0f172a !important;
            font-weight: 600;
            letter-spacing: 0.2px;
          }
          .checkoutTotalBar p:last-child {
            color: #101828 !important;
            font-size: 28px;
            font-weight: 800;
          }
          .checkoutPillBtn {
            border-radius: 999px !important;
            padding: 8px 14px !important;
            min-height: 36px !important;
            box-shadow: none !important;
          }
          .checkoutView .tipBtn {
            background: rgba(15, 23, 42, 0.06) !important;
            color: rgba(15, 23, 42, 0.92) !important;
            border: 1px solid rgba(15, 23, 42, 0.10) !important;
            box-shadow: none !important;
          }
          .checkoutView .tipBtn:hover {
            background: rgba(15, 23, 42, 0.10) !important;
            border-color: rgba(15, 23, 42, 0.18) !important;
          }
          .checkoutView .tipBtnActive {
            background: rgba(15, 23, 42, 0.92) !important;
            color: #ffffff !important;
            border-color: rgba(15, 23, 42, 0.92) !important;
          }
          .checkoutView .MuiOutlinedInput-root {
            border-radius: 12px !important;
            background: #ffffff !important;
          }
          .checkoutView .MuiOutlinedInput-notchedOutline {
            border: 1px solid rgba(15, 23, 42, 0.16) !important;
          }
          .checkoutView .MuiOutlinedInput-root:hover .MuiOutlinedInput-notchedOutline {
            border-color: rgba(15, 23, 42, 0.28) !important;
          }
          .checkoutView .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline {
            border-color: rgba(16, 185, 129, 0.55) !important;
          }
          .checkoutView .MuiOutlinedInput-root.Mui-focused {
            box-shadow: 0 0 0 4px rgba(16, 185, 129, 0.14) !important; /* soft focus glow */
          }
          .checkoutView input, .checkoutView textarea {
            font-size: 14px;
          }
          .checkoutNotesWrap textarea {
            outline: none;
          }
          .checkoutPrimaryBtn {
            width: 100%;
            border-radius: 999px !important;
            min-height: 50px !important;
            font-weight: 800 !important;
            border: 1px solid rgba(255,255,255,.34) !important;
            background:
              linear-gradient(180deg, rgba(var(--color-primary) / .96), rgba(var(--color-primary) / .80)) !important;
            box-shadow:
              0 18px 28px rgba(15,23,42,.16),
              inset 0 1px 0 rgba(255,255,255,.24) !important;
          }

          @media (max-width: 900px) {
            .checkoutShell {
              grid-template-columns: 1fr;
              height: auto;
              min-height: 0;
            }
            .checkoutSide {
              border-left: 0;
              border-top: 1px solid rgba(15, 23, 42, 0.12);
            }
            .checkoutMain {
              padding: 16px 14px;
            }
          }

          @media (max-width: 640px) {
            .checkoutSection {
              grid-template-columns: 1fr;
              gap: 10px;
            }
            .checkoutPaymentGrid {
              grid-template-columns: 1fr;
            }
          }
        `}
      </style>
      <div className="checkoutHeader flex justify-between items-center px-4 pb-2 border-b">
        <div onClick={() => setStage("DETAILS")}>
          <ArrowBackIosNewIcon className="!text-[15px] !text-primary cursor-pointer" />
        </div>
        <h4 className="text-[28px] leading-none font-[800] tracking-[-0.02em] text-[#101828] text-center max-sm:text-[22px]">
          Check Out
        </h4>
        <div />
      </div>

      <div className="checkoutView">
        <div className="checkoutShell">
          <div className="checkoutMain">
            <div className="checkoutCard">
              <div className="checkoutSection">
                <div className="checkoutSectionTitle">Method</div>
                <div className="checkoutSectionBody text-[16px] font-[600] text-[#101828]">
                  {orderType === ORDER.DELIVERY ? "Delivery" : "Pickup"}
                </div>
              </div>

              <div className="checkoutSection">
                <div className="checkoutSectionTitle">
                  {orderType === ORDER.DELIVERY ? "Delivery time" : "Pickup time"}
                </div>
                <div className="checkoutSectionBody text-[16px] font-[700] text-[#101828]">
                  {scheduledLabel}
                </div>
              </div>

              <div className="checkoutSection">
                <div className="checkoutSectionTitle">
                  {orderType === ORDER.DELIVERY ? "Delivery address" : "Pickup address"}
                </div>
                <div className="checkoutSectionBody">
                  {orderType === ORDER.TAKE_OUT && (
                    <p className="text-[16px] font-[700] text-[#101828]">
                      {companyData?.companyName}
                    </p>
                  )}
                  <p className="mt-1 text-[15px] leading-[1.6] text-[#475467]">
                    {customerAddress}
                  </p>
                </div>
              </div>

              <div className="checkoutSection">
                <div className="checkoutSectionTitle">Contact and payment</div>
                <div className="checkoutSectionBody">
                  <div className="checkoutContactCard">
                    <p className="text-[16px] font-[700]">{customerName}</p>
                    <p className="text-[15px] text-[#475467]">{checkoutData?.phoneNo}</p>
                    <p className="text-[15px] text-[#475467]">{checkoutData?.email}</p>
                  </div>

                  <div className="mt-7">
                    <p className="text-[16px] font-[800] text-[#101828]">
                      Payment method
                    </p>
                    <div className="checkoutPaymentGrid">
                      {paymentOptions.map((option) => (
                        <button
                          key={option.id}
                          type="button"
                          className={`checkoutPaymentCard ${
                            selectedPayment === option.id ? "checkoutPaymentCardActive" : ""
                          }`}
                          onClick={() => setSelectedPayment(option.id)}
                        >
                          <div className="flex items-center gap-3">
                            <div>{option.icon}</div>
                            <div>
                              <div className="text-[15px] font-[800]">{option.label}</div>
                              <div className="checkoutMuted mt-1 text-[13px]">
                                {option.description}
                              </div>
                            </div>
                          </div>
                        </button>
                      ))}
                    </div>
                  </div>

                  <div className="checkoutNotesWrap mt-6">
                    <div className="checkoutCard !p-0">
                      <TextArea
                        label="Order Notes"
                        value={notes}
                        onChange={(e) => setnotes(e.target.value)}
                        placeholder="Add any special instructions..."
                      />
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="checkoutSide">
            <Button
              className="checkoutPrimaryBtn"
              disabled={!selectedPaymentOption || unavailableCartItems.length > 0}
              onClick={() => {
                if (!selectedPaymentOption) return;
                if (unavailableCartItems.length) {
                  showUnavailableItemsError();
                  return;
                }

                if (selectedPaymentOption.kind === "counter") {
                  handlePlaceOrder();
                  return;
                }

                dispatch(
                  updateCheckoutData({
                    tip,
                    deliveryCharge,
                    paymentType: selectedPaymentOption.id,
                    paymentTotal: checkoutGrandTotal,
                    specialDiscount,
                  })
                );
                setStage("PAYMENT");
              }}
            >
              {!selectedPaymentOption
                ? "Select Payment Method"
                : selectedPaymentOption.kind === "counter"
                ? orderType === ORDER.DELIVERY
                  ? "Place Delivery Order"
                  : "Place Pickup Order"
                : `Continue with ${selectedPaymentOption.label}`}
            </Button>
            {!!unavailableCartItems.length && (
              <div className="mt-3 rounded-2xl border border-red-200 bg-red-50 px-4 py-3 text-sm font-[700] text-red-600">
                Some items are not available for{" "}
                {orderType === ORDER.DELIVERY ? "Delivery" : "Pickup"}.
              </div>
            )}

            <div className="mt-4">
              {!!checkoutData?.finalBill?.[0]?.amt && (
                <div className="flex justify-between checkoutRow">
                  <p className="font-semibold text-[14px] checkoutMuted">
                    {checkoutData.finalBill[0].title}
                  </p>
                  <p className="font-semibold text-[14px] text-slate-900">
                    CA${Number(checkoutData.finalBill[0].amt || 0).toFixed(2)}
                  </p>
                </div>
              )}

              {!!specialDiscount && (
                <div className="flex justify-between checkoutRow">
                  <p className="font-bold text-[14px] text-[#f10505]">
                    Special Discount ({Number(settings?.OnlineSpecialDiscount || 0)}%)
                  </p>
                  <p className="font-bold text-[14px] text-[#f10505]">
                    -CA${specialDiscount.toFixed(2)}
                  </p>
                </div>
              )}

              {checkoutData?.finalBill?.slice(1).map((i: any) => (
                <>
                  {i.amt > 0 && (
                    <div className="flex justify-between checkoutRow">
                      <p className="font-semibold text-[14px] checkoutMuted">{i.title}</p>
                      <p className="font-semibold text-[14px] text-slate-900">
                        CA${i.amt.toFixed(2)}
                      </p>
                    </div>
                  )}
                </>
              ))}

              {orderType === ORDER.DELIVERY &&
                (isKmDeliveryChargesEnabled(settings) || !!deliveryCharge) && (
                <div className="flex justify-between checkoutRow">
                  <p className="font-semibold text-[14px] checkoutMuted">
                    Delivery Charge
                    {isKmDeliveryChargesEnabled(settings) && deliveryDistance !== null
                      ? ` (${deliveryDistance} km)`
                      : ""}
                  </p>
                  <p className="font-semibold text-[14px] text-slate-900">
                    CA${deliveryCharge.toFixed(2)}
                  </p>
                </div>
              )}

              {!settings.DontDisplayTipOption && (
                <>
                  <div className="mt-5 flex items-center justify-between">
                    <p className="font-semibold text-[18px] text-slate-900">Tip the staff</p>
                    <p className="font-semibold text-[18px] text-slate-900">
                      CA${tip.toFixed(2)}
                    </p>
                  </div>

                  <div className="mt-3 flex items-center gap-2">
                    <Button
                      className={`checkoutPillBtn tipBtn !h-9 !min-w-[70px] !px-5 !py-0 ${isTipActive(tip10) ? "tipBtnActive" : ""}`}
                      onClick={() => updateTip(tip10)}
                    >
                      10%
                    </Button>
                    <Button
                      className={`checkoutPillBtn tipBtn !h-9 !min-w-[70px] !px-5 !py-0 ${isTipActive(tip15) ? "tipBtnActive" : ""}`}
                      onClick={() => updateTip(tip15)}
                    >
                      15%
                    </Button>
                    <Button
                      className={`checkoutPillBtn tipBtn !h-9 !min-w-[70px] !px-5 !py-0 ${isTipActive(tip20) ? "tipBtnActive" : ""}`}
                      onClick={() => updateTip(tip20)}
                    >
                      20%
                    </Button>
                    <Button
                      className="checkoutPillBtn tipBtn !h-9 !min-w-[78px] !px-5 !py-0"
                      onClick={() => {
                        const input = document.querySelector<HTMLInputElement>(
                          ".checkoutTipInput input"
                        );
                        input?.focus();
                        input?.select?.();
                      }}
                    >
                      Other
                    </Button>
                    <div className="checkoutTipInput ml-auto w-[74px] shrink-0">
                      <Input
                        size="small"
                        sx={{
                          width: "100%",
                          "& .MuiInputBase-root": {
                            height: 38,
                            borderRadius: "12px",
                            backgroundColor: "#f8fafc",
                          },
                          "& .MuiInputBase-input": {
                            padding: "8px 12px",
                            fontSize: "14px",
                          },
                        }}
                        value={customTipText}
                        inputProps={{
                          inputMode: "decimal",
                        }}
                        onBlur={normalizeCustomTip}
                        onValueChange={updateCustomTip}
                      />
                    </div>
                  </div>
                </>
              )}

              <div className="checkoutTotalBar flex justify-between mt-6">
                <p>Total</p>
                <p>CA${checkoutGrandTotal.toFixed(2)}</p>
              </div>
              {selectedPaymentOption && (
                <div className="mt-4 rounded-2xl border border-black/10 bg-white px-4 py-3 text-[13px] text-[#475467]">
                  Selected payment:{" "}
                  <span className="font-[800] text-[#101828]">
                    {selectedPaymentOption.label}
                  </span>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default CheckoutView;
